import React from "react";
import MobileTenderTabs from "../club/responsive/MobileTenderTabs";
import TenderTabs from "../club/TenderTabs";
import StaffTenderCard from "../club/StaffTenderCard";
import TenderCard from "../club/TenderCard";
import ListedRequestsListView from "../common/ListedRequestsListView";
import ClubListRowActions from "./ClubListRowActions";
import LifecycleSegment from "./LifecycleSegment";
import RequestDraftBadge from "../club/RequestDraftBadge";
import RequestArchivedBadge from "../club/RequestArchivedBadge";
import ReactivateRequestButton from "../club/ReactivateRequestButton";
import { isArchivedRequest } from "../../utils/tenderFormatters";
import { newestTenderId } from "../../utils/newestTender";
import { showsSourceTabs } from "../../utils/tenderSourceTabs";

const ClubTendersMainContent = ({
  isMobile,
  activeMainTab,
  setActiveMainTab,
  setShowFilterModal,
  searchTerm,
  handleSearchChange,
  activeSubTab,
  setActiveSubTab,
  sortBy,
  handleSortChange,
  activePositionFilter,
  handlePositionFilterChange,
  loading,
  error,
  tenders,
  pagination,
  fetchTenders,
  handleSaveStatusChange,
  viewMode,
  changeViewMode,
  listSavingIds,
  handleListToggleSave,
  // Request drafts
  lifecycleTab = "active",
  onLifecycleChange = null,
  lifecycleCounts = null,
  transferWindow = null,
  onDraftChanged = null,
  canCreateTenders = false,
  navigate = null,
}) => {
  // The segment exists only under "My Requests". On All Listed and on Saved it
  // is not rendered at all — neither list is the club's own row set.
  const showLifecycle = activeMainTab === "my-tenders" && !!onLifecycleChange;
  const isDraftsTab = showLifecycle && lifecycleTab === "drafts";
  const isArchiveTab = showLifecycle && lifecycleTab === "archive";

  // One definition for every responsive mount of the lifecycle segment.
  const lifecycleTabs = [
    { id: "active", label: "Active", count: lifecycleCounts?.active },
    { id: "drafts", label: "Drafts", count: lifecycleCounts?.drafts },
    { id: "archive", label: "Archive", count: lifecycleCounts?.archive },
  ];

  // The TP / TP Lite switch separates club TIERS and therefore belongs to
  // "All Listed Requests" only — see utils/tenderSourceTabs.js. It used to be
  // rendered unconditionally, so it also appeared over the club's own rows and
  // over Saved, where there is nothing to separate.
  const showSourceTabs = showsSourceTabs(activeMainTab);

  return (
    /* Large-monitor shell per the handoff: content capped at 1760px and
       centred. Every class is gated at min-[1760px], so at 375px, 768px, 1440px
       and everything below 1760px the page renders byte-for-byte as before.
       Same arbitrary-breakpoint pattern the agent feed already uses
       (pages/agent-tender/AgentTenderFeed.js:527). */
    <div className=" mx-auto pb-6 min-[1760px]:max-w-[1760px] min-[1760px]:mx-auto">
      {/* Lifecycle segment — Active (n) · Drafts (n) · Archive (n), My
          Requests only. The Archive pill was held back until its query
          existed; it now scopes to the backend's terminal set via
          status=archive, same contract shape as Drafts.

          From 1440px up the segment leaves this own row and joins the
          category-tab row below (leftSlot on TenderTabs) so segment and
          Players/Coaches/Staffs/Others + Cards/List share ONE line (Marin's
          header ticket).

          On the PHONE the segment does not render here at all any more: the
          lifecycle pills are a filter INSIDE "My Requests" and may not sit
          above the source choice (Marin's row order), so the mobile mount
          lives in MobileTenderTabs' lifecycleRow slot, directly under the
          source tabs. On desktop the source row is the page header above
          this component, so the order already holds. */}
      {showLifecycle && !isMobile && (
        <div className="min-[1440px]:hidden">
          <LifecycleSegment
            activeTab={lifecycleTab}
            onChange={onLifecycleChange}
            tabs={lifecycleTabs}
          />
        </div>
      )}

      {/* Sub Tabs */}
      {isMobile ? (
        <MobileTenderTabs
          activeTab={activeMainTab}
          onTabChange={setActiveMainTab}
          onFilterClick={() => setShowFilterModal(true)}
          searchTerm={searchTerm}
          handleSearchChange={handleSearchChange}
          onSubTabChange={setActiveSubTab}
          activeSubTab={activeSubTab}
          /* Pro / Youth. MobileTenderTabs has always declared these two props
             (:8-9) and bound them to its category dropdown (:197-198), but the
             mount never handed them over, so the dropdown called an undefined
             onChange and the phone had no way to reach youth requests at all.
             Same pair the desktop header already receives (ClubTenders.js). */
          sortBy={sortBy}
          handleSortChange={handleSortChange}
          activePositionFilter={activePositionFilter}
          handlePositionFilterChange={handlePositionFilterChange}
          /* Cards / List on the phone. The very same pair already goes to the
             desktop row below; without it the switch existed on desktop only,
             and list mode — reachable via ?view_mode=list and remembered in
             localStorage by useViewMode — was a one-way street on a phone. */
          viewMode={viewMode}
          onViewModeChange={changeViewMode}
          hideTenderTabs={!showSourceTabs}
          /* Marin's row order on the phone: source tabs first, the lifecycle
             pills directly under them (only while My Requests is the source),
             then tools, then the category dropdown. */
          lifecycleRow={
            showLifecycle ? (
              <LifecycleSegment
                activeTab={lifecycleTab}
                onChange={onLifecycleChange}
                tabs={lifecycleTabs}
              />
            ) : null
          }
        />
      ) : (
        <TenderTabs
          activeTab={activeSubTab}
          onTabChange={setActiveSubTab}
          /* The prop already existed for exactly this purpose; it was simply
             never wired here. Position pills and the Cards/List toggle are
             unaffected — only the TP / TP Lite pair is dropped. */
          hideTenderTabs={!showSourceTabs}
          isBgWhite={true}
          isFromClub={true}
          onPositionFilterChange={handlePositionFilterChange}
          activePositionFilter={activePositionFilter}
          viewMode={viewMode}
          onViewModeChange={changeViewMode}
          /* The 1440px-and-up mount of the lifecycle segment (see the own-row
             mount above for the rule). min-w-0 + flex-1 let the pill strip
             shrink and scroll inside the shared row instead of pushing the
             category pills off it. */
          leftSlot={
            showLifecycle ? (
              <div className="hidden min-[1440px]:flex min-w-0 flex-1">
                <LifecycleSegment
                  variant="inline"
                  activeTab={lifecycleTab}
                  onChange={onLifecycleChange}
                  tabs={lifecycleTabs}
                />
              </div>
            ) : null
          }
        />
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Error State */}
      {error && <div className="text-red-500 text-center py-8">{error}</div>}

      {/* Tenders Grid */}
      {!loading && !error && (
        <>
          {tenders.length === 0 && isDraftsTab ? (
            /* The Drafts tab explains what a draft is FOR, because an empty
               Drafts tab is the moment the club has never used one. */
            <div className="mx-4 md:mx-12 mt-4 flex flex-col items-center gap-2 rounded-2xl border border-[#E2E8F0] bg-white px-6 py-16 text-center">
              <div className="text-xl font-semibold text-[#0B0B0B]">
                No drafts yet
              </div>
              <p className="max-w-md text-sm leading-relaxed text-[#535370]">
                Save a request as a draft to plan it now and publish it later,
                or give it a date and let it post itself.
              </p>
              {canCreateTenders && navigate && (
                <button
                  type="button"
                  onClick={() => navigate("/club/tenders/create")}
                  className="mt-3 min-h-[44px] rounded-lg bg-[#2283FF] px-5 text-sm font-semibold text-white hover:bg-blue-600"
                >
                  Create Request
                </button>
              )}
            </div>
          ) : tenders.length === 0 && isArchiveTab ? (
            /* Archive empty state per the handoff: what the archive does and
               how long it keeps things. Deliberately NO call to action — an
               empty archive needs nothing from the club. */
            <div className="mx-4 md:mx-12 mt-4 flex flex-col items-center gap-2 rounded-2xl border border-[#E2E8F0] bg-white px-6 py-16 text-center">
              <div className="text-xl font-semibold text-[#0B0B0B]">
                No archived requests
              </div>
              <p className="max-w-md text-sm leading-relaxed text-[#535370]">
                Closed requests are kept here for 12 months and can be
                reactivated at any time.
              </p>
            </div>
          ) : tenders.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {activeMainTab === "saved"
                ? "No saved Requests found"
                : "No Requests found"}
            </div>
          ) : viewMode === "list" ? (
            // Clean accordion list (the layout Marin liked) + the card's
            // per-tab CTA via ClubListRowActions, which reuses the shared
            // useClubTenderActions handlers + the same permission gates:
            // My Requests → View pitches; All/Saved → Message Club (gated).
            // Row save uses the club save handler. Newest badge on the newest.
            <ListedRequestsListView
              tenders={tenders}
              /* Same rule the card has carried since e0dfbba8: a club does not
                 bookmark its OWN request — "shown when browsing other clubs'
                 requests (and in the Saved view)" (club/TenderCard.js:413-415).
                 The list view was added later and passed the handler on every
                 tab, so the two views of the same rows disagreed and a club
                 could file its own request under Saved. Gating the PROP, not
                 the shared component: ListedRequestsListView already renders
                 the button only `{onToggleSave && …}` (:286), and it is shared
                 with the agent view (pages/agent/AgentTenders.js:2408), which
                 has no "my requests" tab and must keep saving. */
              onToggleSave={
                activeMainTab === "my-tenders" ? undefined : handleListToggleSave
              }
              savingIds={listSavingIds || new Set()}
              newestId={isDraftsTab || isArchiveTab ? null : newestTenderId(tenders)}
              /* Draft / Scheduled and Archived pills travel into list mode
                 too; each renders nothing when the row is not its state. The
                 Archived pill is additionally gated to My Requests — Saved
                 also contains terminal rows, and that surface must render
                 exactly as before. */
              renderBadges={(tender) => (
                <>
                  <RequestDraftBadge tender={tender} />
                  {activeMainTab === "my-tenders" && (
                    <RequestArchivedBadge tender={tender} />
                  )}
                </>
              )}
              /* Archived rows carry Reactivate in the collapsed row's actions
                 column too (handoff list row), same confirm as everywhere. */
              renderRowActions={(tender) =>
                activeMainTab === "my-tenders" && isArchivedRequest(tender) ? (
                  <ReactivateRequestButton
                    tender={tender}
                    onChanged={onDraftChanged}
                    size="sm"
                  />
                ) : null
              }
              renderActions={(tender) => (
                <ClubListRowActions
                  tender={tender}
                  type={activeMainTab}
                  transferWindow={transferWindow}
                  onDraftChanged={onDraftChanged}
                />
              )}
            />
          ) : (
            /* min-[1024px] instead of lg: on purpose, and it is the same
               1024px breakpoint (the config keeps Tailwind's defaults). Named
               and arbitrary variants land in different places in the generated
               stylesheet: with `lg:grid-cols-3` the later `lg` rule won over
               `min-[1760px]:grid-cols-4` and the fourth column never appeared
               (measured at 2560: three columns). Written as two arbitrary
               variants they sort by their own min-width, so 1760 wins over
               1024. Below 1760px the rendered result is byte-for-byte the old
               one. */
            <div className="grid grid-cols-1 md:grid-cols-2 min-[1024px]:grid-cols-3 min-[1760px]:grid-cols-4 gap-6 md:px-12 px-4 mt-4 md:mt-0">
              {(() => {
                /* NEWEST marks the freshest row in a FEED. Drafts and
                   archived rows are in no feed, so neither tab hands out that
                   badge — their own status pill is the only marker there. */
                const newestId =
                  isDraftsTab || isArchiveTab ? null : newestTenderId(tenders);
                /* The template follows the ROW's own tender_category, not the
                   selected pill. On the listed/own tabs both read the same,
                   because the list endpoint scopes the result to the chosen
                   category — but a saved staff request could arrive under the
                   "Players" pill and was drawn as a player card with empty
                   fields. Same discriminator the agent grid already uses
                   (pages/agent/AgentTenders.js:2429). */
                return tenders.map((tender) =>
                  tender.tender_category === "staff" ? (
                    <StaffTenderCard
                      key={tender.id}
                      tender={tender}
                      type={activeMainTab}
                      isFromClub={activeMainTab === "my-tenders"}
                      onSaveStatusChange={handleSaveStatusChange}
                      isNewest={tender.id === newestId}
                      transferWindow={transferWindow}
                      onDraftChanged={onDraftChanged}
                    />
                  ) : (
                    <TenderCard
                      key={tender.id}
                      tender={tender}
                      type={activeMainTab}
                      isFromClub={activeMainTab === "my-tenders"}
                      onSaveStatusChange={handleSaveStatusChange}
                      isNewest={tender.id === newestId}
                      transferWindow={transferWindow}
                      onDraftChanged={onDraftChanged}
                    />
                  )
                );
              })()}
            </div>
          )}

          {/* Pagination */}
          {pagination.count > 10 && (
            <div className="flex justify-center items-center gap-2 mt-8">
              <button
                onClick={() => fetchTenders(pagination.currentPage - 1)}
                disabled={!pagination.previous}
                className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="px-4 py-2">
                Page {pagination.currentPage} of{" "}
                {Math.ceil(pagination.count / 10)}
              </span>
              <button
                onClick={() => fetchTenders(pagination.currentPage + 1)}
                disabled={!pagination.next}
                className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ClubTendersMainContent;

